import type { Settings } from "./types";
import { defaultSettings } from "./defaults";
import { SETTINGS_STORAGE_KEY } from "./persist";

type Listener = (s: Settings) => void;

function safeParse(json: string | null): any {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function migrate(raw: any): Settings {
  // If nothing stored, return defaults
  if (!raw || typeof raw !== "object") return { ...defaultSettings };

  // Merge known keys. Anything missing -> default.
  const merged: Settings = {
    ...defaultSettings,
    ...raw,
  };

  // Sanitize some fields to avoid broken types
  if (!Array.isArray(merged.deadlineReminderMinutes)) merged.deadlineReminderMinutes = [...defaultSettings.deadlineReminderMinutes];
  merged.deadlineReminderMinutes = merged.deadlineReminderMinutes
    .map((x) => Number(x))
    .filter((x) => Number.isFinite(x) && x > 0)
    .slice(0, 10);

  if (!Array.isArray(merged.hiddenCourses)) merged.hiddenCourses = [];
  merged.hiddenCourses = merged.hiddenCourses
    .map((x) => String(x))
    .filter(Boolean)
    .slice(0, 500);

  if (typeof merged.selectedGroup !== "string") merged.selectedGroup = "";
  if (typeof merged.icsUrl !== "string") merged.icsUrl = "";
  if (typeof merged.customGlassColor !== "string") merged.customGlassColor = defaultSettings.customGlassColor;
  if (typeof merged.hideOnlineLessons !== "boolean") merged.hideOnlineLessons = defaultSettings.hideOnlineLessons;

  if (![5, 10, 30, 60].includes(merged.autoRefreshMinutes as any)) {
    merged.autoRefreshMinutes = defaultSettings.autoRefreshMinutes;
  }

  return merged;
}

class SettingsStore {
  private listeners = new Set<Listener>();
  private state: Settings;

  constructor() {
    const raw = safeParse(localStorage.getItem(SETTINGS_STORAGE_KEY));
    this.state = migrate(raw);
    // persist migrated state
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.state));
  }

  get(): Settings {
    return this.state;
  }

  set(patch: Partial<Settings> | ((prev: Settings) => Settings)) {
    const next =
      typeof patch === "function"
        ? (patch as (prev: Settings) => Settings)(this.state)
        : { ...this.state, ...patch };

    this.state = migrate(next);
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.state));
    this.listeners.forEach((l) => l(this.state));
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    // immediately call with current state
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  reset() {
    this.state = { ...defaultSettings };
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.state));
    this.listeners.forEach((l) => l(this.state));
  }
}

export const settingsStore = new SettingsStore();
